interface TrendChartProps {
  current: number;
  delta: number;
  months: string[];
  /**
   * Y values for SVG polyline points (smaller = higher on chart since SVG y is inverted).
   * The original chart uses values from 22 (high score) to 52 (low score) over a 0-80 viewBox.
   */
  points: number[];
}

export function TrendChart({ current, delta, months, points }: TrendChartProps) {
  const width = 280;
  const height = 80;
  const stepX = width / (points.length - 1);
  const polyline = points.map((y, i) => `${i * stepX},${y}`).join(' ');
  const lastPoint = points[points.length - 1];

  const areaPath = [
    ...points.map((y, i) => `${i === 0 ? 'M' : 'L'}${i * stepX},${y}`),
    `L${width},${height}`,
    `L0,${height}`,
    'Z',
  ].join(' ');

  const positive = delta >= 0;
  const lineColor = positive ? 'var(--c-ok)' : 'var(--c-crit)';
  const deepColor = positive ? 'var(--c-ok-deep)' : 'var(--c-crit-deep)';

  return (
    <div className="glass-panel p-[18px] px-5">
      <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted mb-2.5">
        Optimal Score promedio
      </h3>
      <div className="flex items-baseline gap-3 mb-3.5">
        <span className="text-[28px] font-bold tracking-[-0.025em] tnum leading-none">
          {current}
        </span>
        <span className={`text-[13px] font-semibold ${positive ? 'text-ok-deep' : 'text-crit-deep'}`}>
          {positive ? '↑' : '↓'} {positive ? '+' : ''}{delta}
          <span className="text-tertiary font-normal"> en 6 meses</span>
        </span>
      </div>
      <svg
        className="w-full h-20 block"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.16" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </linearGradient>
          <filter id="trendGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={areaPath} fill="url(#trendGrad)" />
        <polyline
          points={polyline}
          fill="none"
          stroke={lineColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#trendGlow)"
        />
        <circle cx={width} cy={lastPoint} r={3.5} fill={deepColor} filter="url(#trendGlow)" />
      </svg>
      <div className="flex justify-between mt-1.5 text-[10px] text-faint tnum">
        {months.map((m) => (
          <span key={m}>{m}</span>
        ))}
      </div>
    </div>
  );
}
